import { Link, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { AppShellV8 } from "../AppShellV8";
import { useActiveClient, getCurrentMemberId } from "../activeClient";
import { personFor } from "../people";
import { useRoster, useEnsureRoster } from "../roster";
import { groupTasksByMeeting } from "../taskGrouping";
import { TaskDrawer } from "../TaskDrawer";
import { isMine, canApprove } from "../reviewState";
import { resolveDueIso, friendlyDue, friendlyDate } from "../dueDates";
import { matchName } from "../nameMatch";
import { PersonFilter } from "../PersonFilter";
import { FriendlyLoading } from "../FriendlyLoading";

// A trimmed review item for the My Tasks "pending review" lens (D4) — my slice only.
interface ReviewLite {
  id: number;
  title: string;
  reviewState?: string | null;
  reviewer?: string | string[] | null;
  submittedBy?: string | null;
  routedTo?: string | null;
}

/**
 * My Tasks (AMB-346 / C2) — the person-first "show me my work" view a VA reaches via
 * Home's "All tasks →". A flat list of open to-dos across clients, defaulting to the
 * signed-in member, consuming GET /api/agents/actions. Each row opens its "task page":
 * the meeting digest scrolled to that to-do (C1). This replaces the old "All tasks →"
 * that dumped users on the Meetings list (the confusion Hannah hit).
 */

interface ApiAction {
  id: string;
  task: string;
  owner?: string | null;
  due?: string | null;
  status?: string | null;
  client?: string | null;
  sourceMeeting?: string | null;
  difficulty?: number;
}
interface ApiDeliverable {
  id: string;
  title: string;
  sourceAction?: string | null;
}

interface TaskRow {
  id: string;
  task: string;
  owner: string;
  client: string | null;
  due: string;
  dueIso: string | null;
  difficulty: number;
  href: string;
  doc?: { id: string; title: string };
  // Source meeting (for grouping My Tasks by meeting, latest first — AMB-366).
  meetingId: string | null;
  meetingName?: string;
  meetingDate?: string | null;
}

const firstName = (owner?: string | null) => String(owner || "").trim().split(/[\s/]+/)[0].toLowerCase();
const isoDate = (due?: string | null): string | null => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(due || ""));
  return m ? m[1] : null;
};
const diffBand = (d: number) =>
  d <= 2 ? { fill: "bg-green-500", light: "bg-green-100" } : d <= 3 ? { fill: "bg-amber-500", light: "bg-amber-100" } : { fill: "bg-red-500", light: "bg-red-100" };

function DiffBars({ d }: { d: number }) {
  const b = diffBand(d);
  return (
    <span className="inline-flex gap-0.5 align-middle">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`h-1.5 w-3 rounded-full ${i < d ? b.fill : b.light}`} />
      ))}
    </span>
  );
}

export function Tasks() {
  const slug = useActiveClient().slug;
  useEnsureRoster();
  const roster = useRoster(); // this tenant's own members (AMB-438) — for the person filter
  const [person, setPerson] = useState<string>(() => getCurrentMemberId() ?? "all"); // default = signed-in member; "all" (Everyone) when the login isn't a team member
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const base = `${DOCS_API_DOMAIN}/api/agents`;
    Promise.all([
      fetch(`${base}/actions?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
      fetch(`${base}/deliverables?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
      fetch(`${base}/meetings?client=${slug}`).then((r) => (r.ok ? r.json() : { items: [] })),
    ])
      .then(([actions, dels, meetings]) => {
        if (cancelled) return;
        const draftByAction = new Map<string, { id: string; title: string }>();
        for (const d of (dels.items || []) as ApiDeliverable[]) {
          if (d.sourceAction && !draftByAction.has(d.sourceAction)) draftByAction.set(d.sourceAction, { id: d.id, title: d.title });
        }
        // Resolve each task's source meeting → its name + date, for grouping by meeting.
        const meetingById = new Map<string, { title: string; date: string | null }>();
        for (const m of (meetings.items || []) as { id: string; title?: string; date?: string | null }[]) {
          meetingById.set(m.id, { title: m.title || m.id, date: m.date || null });
        }
        const out: TaskRow[] = ((actions.items || []) as ApiAction[]).map((a) => {
          const mtg = a.sourceMeeting ? meetingById.get(a.sourceMeeting) : undefined;
          return {
            id: a.id,
            task: a.task,
            owner: a.owner || "",
            client: a.client || null,
            due: a.due || "",
            // Sort key (AMB-393): prefer a real leading ISO; otherwise resolve the free-text
            // due against the source meeting so "this week"/"Friday" dues order correctly too
            // (not just label correctly) even before the generation-side date backfill lands.
            dueIso: isoDate(a.due) || resolveDueIso(a.due, mtg?.date ?? null),
            difficulty: typeof a.difficulty === "number" ? a.difficulty : 2,
            // C1: open the task's page (its meeting digest scrolled to this to-do).
            href: a.sourceMeeting ? `/agents/digest?id=${encodeURIComponent(a.sourceMeeting)}&task=${encodeURIComponent(a.id)}` : "/agents/meetings",
            doc: draftByAction.get(a.id),
            meetingId: a.sourceMeeting || null,
            meetingName: mtg?.title,
            meetingDate: mtg?.date ?? null,
          };
        });
        setRows(out);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null); // AMB-408/409/410: deep-linked task to scroll to + highlight
  const [done, setDone] = useState<Set<string>>(new Set());
  const [lens, setLens] = useState<"tasks" | "review">("tasks");
  const [reviewRows, setReviewRows] = useState<ReviewLite[]>([]);
  const me = getCurrentMemberId();
  const location = useLocation();

  // D4: the "pending review" lens = MY slice only (items I submitted + items awaiting my
  // approval). The full Reviews Center is untouched; this just re-reads the same data,
  // filtered to me, so the whole core path lives in My Tasks.
  useEffect(() => {
    let cancelled = false;
    fetch(`${DOCS_API_DOMAIN}/api/agents/reviews?client=${slug}`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => { if (!cancelled) setReviewRows(((d.items || []) as ReviewLite[]).filter((r) => isMine(r, me))); })
      .catch(() => { if (!cancelled) setReviewRows([]); });
    return () => { cancelled = true; };
  }, [slug, me]);

  // D5 + AMB-408/409/410: deep-link via /agents/tasks?open=<taskId> — from the Home hero
  // widget AND from the Calendar (Tasks-Due, week all-day, agenda). Focus the task: if it's
  // hidden by the person filter, drop to "Everyone" so it shows; scroll + highlight it; and
  // if it has a draft, open its drawer too.
  useEffect(() => {
    const want = new URLSearchParams(location.search).get("open");
    if (!want) return;
    const target = rows.find((r) => r.id === want);
    if (!target) return;
    if (person !== "all" && firstName(target.owner) !== person) setPerson("all");
    setFocusId(want);
    if (target.doc) setOpenTaskId(want);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, rows]);

  // Scroll the deep-linked task into view + briefly highlight it, then clear the highlight.
  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`task-${focusId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setFocusId(null), 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, rows, person]);

  const mine = useMemo(
    () => rows.filter((r) => person === "all" || firstName(r.owner) === person),
    [rows, person],
  );
  // Group by source meeting, latest meeting first; within a meeting, by due date (module 8).
  const groups = useMemo(() => groupTasksByMeeting(mine), [mine]);
  // Drafted tasks (in display order) drive the drawer's prev/next cycling.
  const draftTasks = useMemo(() => groups.flatMap((g) => g.items).filter((r) => r.doc), [groups]);
  const openTask = openTaskId ? mine.find((r) => r.id === openTaskId) : null;

  // Toggle a to-do done/not-done from the list (manual complement to approval auto-complete).
  // Writes the action's status via the generic entity updater; clicking again reopens it.
  const markDone = (id: string) => {
    const willBeDone = !done.has(id);
    setDone((prev) => {
      const next = new Set(prev);
      if (willBeDone) next.add(id);
      else next.delete(id);
      return next;
    });
    fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: slug, type: "action", id, fields: { status: willBeDone ? "done" : "open" } }),
    }).catch(() => {/* optimistic */});
  };

  return (
    <AppShellV8 breadcrumb={["My Tasks"]}>
      <div className="flex h-full flex-col">
        <div className="flex flex-wrap items-center gap-3 border-b border-black/[0.06] px-5 py-3">
          <h1 className="text-base font-semibold tracking-tight text-gray-900">All My Tasks</h1>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500">{lens === "tasks" ? mine.length : reviewRows.length}</span>
          {/* D4: lens toggle — Tasks vs my Pending-review slice (Reviews Center untouched). */}
          <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5 text-[12px]">
            <button onClick={() => setLens("tasks")} className={`rounded-md px-2.5 py-1 font-medium ${lens === "tasks" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>Tasks</button>
            <button onClick={() => setLens("review")} className={`rounded-md px-2.5 py-1 font-medium ${lens === "review" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}>Pending review</button>
          </div>
          {lens === "tasks" && (
            <PersonFilter
              className="ml-auto"
              value={person}
              onChange={setPerson}
              options={roster.map((m) => ({ id: m.id, name: m.name }))}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {lens === "review" ? (
            <div className="mx-auto max-w-3xl space-y-2">
              {reviewRows.length === 0 ? (
                <p className="text-[13px] text-gray-400">Nothing in your review slice — items you submit or are asked to approve show here.</p>
              ) : (
                reviewRows.map((r) => {
                  const awaitingMe = canApprove(r, me);
                  return (
                    <div key={r.id} className="flex items-center gap-3 rounded-xl bg-white p-3.5 shadow-sm ring-1 ring-black/[0.04]">
                      <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${awaitingMe ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>{awaitingMe ? "Approve" : "Submitted"}</span>
                      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-gray-900">{r.title}</span>
                      <span className="shrink-0 text-[11px] text-gray-400">{r.reviewState || "—"}</span>
                      <Link to="/agents/reviews" className="shrink-0 rounded-lg border border-black/[0.1] px-2.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50">Open in Reviews</Link>
                    </div>
                  );
                })
              )}
            </div>
          ) : loading ? (
            <FriendlyLoading message="Gathering your tasks…" />
          ) : mine.length === 0 ? (
            <p className="text-[13px] text-gray-400">No open to-dos{person === "all" ? "" : " for you"} right now.</p>
          ) : (
            <div className="mx-auto max-w-3xl space-y-6">
              {groups.map((g) => (
                <div key={g.meetingId ?? "_none"}>
                  {/* Meeting group header — latest meeting first */}
                  <div className="mb-2 flex items-center gap-2">
                    <h2 className="text-[12px] font-semibold uppercase tracking-wide text-gray-500">{g.meetingName}</h2>
                    {g.meetingDate && <span className="text-[11px] text-gray-400">{friendlyDate(g.meetingDate)}</span>}
                    <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-400">{g.items.length}</span>
                    <span className="h-px flex-1 bg-black/[0.05]" />
                  </div>
                  <div className="space-y-2">
                    {g.items.map((r) => {
                      // Fresh date label (module 6): use the stored ISO if present, else
                      // resolve the free-text due against the meeting; render relative to now.
                      const iso = r.dueIso || resolveDueIso(r.due, r.meetingDate ?? null);
                      const todayIso = new Date().toISOString().slice(0, 10);
                      const dueLabel = iso ? friendlyDue(iso, todayIso) : r.due;
                      const overdueSoon = iso ? iso <= todayIso : false;
                      const isDone = done.has(r.id);
                      // Resolve the owner to a roster member where confident (module 7);
                      // outsiders keep their legible name (no broken slug/initials avatar).
                      const owner = personFor(matchName(r.owner, roster));
                      return (
                        <div key={r.id} id={`task-${r.id}`} className={`group relative flex items-center gap-3 rounded-xl bg-white p-3.5 shadow-sm transition-all duration-150 hover:shadow ${focusId === r.id ? "ring-2 ring-blue-400" : "ring-1 ring-black/[0.04]"}`}>
                          {/* Mark done — complete a task in place without opening anything (D3) */}
                          <button
                            onClick={() => markDone(r.id)}
                            title={isDone ? "Mark not done" : "Mark done"}
                            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${isDone ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300 text-transparent hover:border-emerald-400"}`}
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          </button>
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${owner.color} text-[9px] font-bold text-white`} title={owner.name}>{owner.initials}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="shrink-0 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">To-do</span>
                              <span className={`truncate text-[13px] font-medium ${isDone ? "text-gray-400 line-through" : "text-gray-900"}`}>{r.task}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                              <DiffBars d={r.difficulty} />
                              {r.doc ? (
                                // AMB-417: the blue draft link is clickable — opens the See-draft drawer.
                                <button
                                  onClick={() => setOpenTaskId(r.id)}
                                  className="truncate text-left text-blue-600 hover:text-blue-700 hover:underline"
                                  title="See the draft"
                                >
                                  {r.doc.title}
                                </button>
                              ) : <span>No draft</span>}
                            </div>
                          </div>
                          {dueLabel && (
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 transition-opacity duration-150 group-hover:opacity-0 ${overdueSoon ? "bg-amber-50 text-amber-700 ring-amber-200/60" : "bg-gray-50 text-gray-600 ring-gray-200/60"}`}>{dueLabel}</span>
                          )}
                          {/* Hover affordances — explicit verbs, no ambiguous whole-row click (D3).
                              AMB-396: high-contrast, obviously-clickable buttons. The previous
                              "Go to meeting" was white-on-white (barely visible); both now have a
                              solid backdrop, clear borders, an icon, and a larger hit target. */}
                          <span className="pointer-events-none absolute right-2 z-10 flex items-center gap-2 rounded-xl bg-white py-1 pl-6 pr-1 opacity-0 shadow-[-12px_0_12px_-6px_rgba(255,255,255,0.95)] ring-1 ring-black/[0.06] transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100">
                            {r.doc && (
                              <button onClick={() => setOpenTaskId(r.id)} className="inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-[12px] font-semibold text-white shadow-sm hover:bg-gray-700">
                                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                See draft
                              </button>
                            )}
                            <Link to={r.href} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-gray-100 px-3 py-1.5 text-[12px] font-semibold text-gray-800 shadow-sm hover:bg-gray-200">
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25V15M15 3.75h5.25m0 0v5.25m0-5.25L9.75 14.25" /></svg>
                              Go to meeting
                            </Link>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {openTask && (
        <TaskDrawer
          task={openTask}
          draftTasks={draftTasks}
          onClose={() => setOpenTaskId(null)}
          onNavigate={(id) => setOpenTaskId(id)}
          onMarkDone={markDone}
        />
      )}
    </AppShellV8>
  );
}
